import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { notify } from '../../app';
import { useFetch, useRequest } from '../../hooks';
import { NavRoutes } from '../../app/Routes/const';
import { useCourseFinish } from './useCourseFinish';
import { completeStep, getStepDetails } from '../../api';
import { generateMindMap } from '../../components/MindMap/utils/initialElements';
import type { CourseType, EnrollmentType } from './useCourse';

const getFirstStep = (
  course: CourseType,
  enrollment: EnrollmentType,
  stepFromParams: string | null
) => {
  return (
    stepFromParams ||
    course?.modules
      ?.flatMap(m => m.steps)
      .find(l => !enrollment.completedSteps?.includes(l?.id!))?.id ||
    course?.modules?.[0].steps?.[0]?.id
  );
};

export const useCourseDetails = (
  course: CourseType,
  enrollment: EnrollmentType,
  onRefresh: () => void
) => {
  const id = course?.id;

  const history = useHistory();
  const stepFromParams = new URLSearchParams(useLocation().search).get('step');
  const trackFinish =
    new URLSearchParams(useLocation().search).get('track_finish') === 'true';
  const { isOpened: isFinishOpened, onClose: onHideFinish } = useCourseFinish(
    enrollment,
    trackFinish
  );

  const [stepId, setStepId] = useState<string | undefined>(() =>
    getFirstStep(course, enrollment, stepFromParams)
  );
  const handleSetStep = (step?: string, withCleanup: boolean = true) => {
    if (!step) return;

    setStepId(step);

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('step', step);
    withCleanup && searchParams.delete('track_finish');
    history.push({ search: searchParams.toString() });
  };

  useEffect(
    () => {
      handleSetStep(getFirstStep(course, enrollment, stepFromParams), false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { intializing: loading, data: stepDetails } = useFetch(async () => {
    if (!id || !stepId) return null;

    const details = await getStepDetails(id, stepId);

    return {
      ...details,
      mindMap: details?.mindmap && generateMindMap(details?.mindmap),
    };
  }, [id, stepId]);

  const [handleCompleteStep, updating] = useRequest(
    () => {
      if (!enrollment.id || !stepId) return;
      if (enrollment.completedSteps?.includes(stepId)) return;

      return completeStep(enrollment.id, stepId);
    },
    {
      deps: [enrollment.id, stepId],
      onSuccess: () => {
        onRefresh?.();
      },
      onError: (error: any) => {
        notify.error(
          `Failed to complete step: ${error?.body?.detail ?? 'No details'}`
        );
      },
    }
  );

  const steps = course.modules?.flatMap?.(c => c.steps?.map?.(s => s.id) ?? []);
  const currentStepIndex = steps?.findIndex?.(s => s === stepDetails?.id) || 0;
  const nextStepId = steps?.[currentStepIndex + 1];
  const hasNext = !!nextStepId;

  const handleNextLesson = () => {
    nextStepId && handleSetStep(nextStepId);
  };

  const handleStartQuiz = (stepId: string) => {
    history.push(`${NavRoutes.QUIZ}/${enrollment.id}/${stepId}`);
  };

  return {
    stepId,
    loading,
    updating,
    hasNext,
    stepDetails,

    handleSetStep,
    handleNextLesson,
    handleCompleteStep,
    handleStartQuiz,

    isFinishOpened,
    onHideFinish,
  };
};

export type { CourseType, LessonType } from '../../api';
